import { AsyncButton, Input, Progress, showConfirmationPrompt } from '@bpinternal/ui-kit-next'
import { useState } from 'react'
import { useDetailedUsage, useUpdateAiSpend } from '~/hooks'
import { AnalyticEvent, trackEvent } from '~/providers/SegmentProvider'
import { formatters, NANO_DOLLARS_PER_DOLLAR } from '~/utils'
import { useActiveSubscription } from '../hooks'
import { useCreateSubscription } from '../hooks/useCheckoutSession'
import { usePlanProducts } from '../hooks/usePlanProducts'
import { useSuspenseQuery } from '~/services'

type AiSpendSectionProps = {
  workspaceId: string
}

export const AiSpendSection = ({ workspaceId }: AiSpendSectionProps) => {
  const { currentPlan } = usePlanProducts(workspaceId)
  const {
    data: { billingReadonly },
  } = useSuspenseQuery('workspaces_/$workspaceId_/billing/info', { workspaceId })

  const { monthly: monthlyActiveSubscription } = useActiveSubscription(workspaceId)

  const { ai_spend } = useDetailedUsage({
    workspaceId,
    quotas: ['ai_spend'],
    period: new Date().toISOString().split('T')[0],
  })
  const { mutate: updateAiSpendQuota, isPending: isUpdatingAiSpendQuota } = useUpdateAiSpend()
  const { mutate: createSubscription } = useCreateSubscription(workspaceId)

  const [aiSpendNano, setAiSpendNano] = useState(ai_spend.quota)
  const [aiSpendError, setAiSpendError] = useState<string>()
  const [aiSpendInput, setAiSpendInput] = useState(formatters.nanodollars(ai_spend.quota))

  const handleUpdateAiSpendQuota = async () => {
    const baseEvent: AnalyticEvent = {
      type: 'update_ai_spend',
      workspaceId,
      newAiSpend: aiSpendNano / NANO_DOLLARS_PER_DOLLAR,
      prevAiSpend: ai_spend.quota / NANO_DOLLARS_PER_DOLLAR,
      plan: currentPlan.legacyName,
      state: 'clicked',
    } as const

    if (monthlyActiveSubscription) {
      trackEvent({ ...baseEvent, state: 'confirmed' })
      updateAiSpendQuota({
        workspaceId,
        monthlySpendingLimit: aiSpendNano / NANO_DOLLARS_PER_DOLLAR,
        onError: () => {
          setAiSpendInput(formatters.nanodollars(ai_spend.quota))
        },
      })
    } else {
      const confirm = await showConfirmationPrompt(<p>Add a payment method to increase your AI Spend Budget</p>, {
        title: 'Payment Method Required',
        confirmLabel: 'Add Payment Method',
      })
      trackEvent({ ...baseEvent, state: 'credit_card_required' })
      if (confirm) createSubscription()
    }
  }

  const handleAiSpendInput = (value: string) => {
    if (!value.startsWith('$')) return

    const dollarCount = (value.match(/\$/g) || []).length
    if (dollarCount > 1) return

    const numberValue = value.slice(1)
    setAiSpendInput(numberValue)

    if (isNaN(Number(numberValue)) || numberValue.trim() === '') {
      setAiSpendError('Please enter a valid number')
      return
    }
    const cleanNumber = Number.parseFloat(numberValue)
    if (cleanNumber < 5) {
      setAiSpendError('AI Spend must be greater than $5')
      return
    }

    const roundedNumber = Math.round(cleanNumber * 100) / 100

    setAiSpendError(undefined)
    setAiSpendNano(roundedNumber * NANO_DOLLARS_PER_DOLLAR)
  }

  return (
    <div>
      <div className="pb-6 font-medium sm:pb-2">AI Spend</div>
      <div className="mb-4 flex w-full flex-col flex-wrap gap-4 rounded-lg bg-neutral-50 p-4 sm:mb-1 sm:flex-row sm:bg-inherit sm:p-0">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Input
              value={`$${aiSpendInput}`}
              onChange={(e) => {
                handleAiSpendInput(e.target.value)
              }}
              onBlur={() => {
                setAiSpendError(undefined)
                setAiSpendInput(formatters.nanodollars(aiSpendNano))
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAiSpendError(undefined)
                  setAiSpendInput(formatters.nanodollars(aiSpendNano))
                }
              }}
              className={'w-full pr-4 sm:w-24'}
            />
            <div className="whitespace-nowrap text-sm font-medium">per month</div>
          </div>
          <div className="min-h-6 text-xs text-danger">{aiSpendError}</div>
        </div>
        <AsyncButton
          disabled={
            (!!monthlyActiveSubscription && (!!aiSpendError || ai_spend.quota === aiSpendNano)) ||
            isUpdatingAiSpendQuota ||
            billingReadonly
          }
          onClick={() => {
            handleUpdateAiSpendQuota()
          }}
          loading={isUpdatingAiSpendQuota}
          className="bg-primary"
        >
          {'Update Budget'}
        </AsyncButton>
      </div>
      <div className="pb-4 text-sm text-muted-foreground">
        The first $5 each month is free. Usage beyond that is billed at provider cost, with no markup.{' '}
      </div>
      <div className="flex w-full justify-end pb-2 text-xs text-muted-foreground">{`${formatters.$nanodollars(ai_spend.value)} of ${formatters.$nanodollars(ai_spend.quota)} used`}</div>
      <Progress
        value={(ai_spend.value * 100) / ai_spend.quota}
        className="bg-neutral-200 [&>div]:rounded-lg [&>div]:bg-purple-500"
      ></Progress>
    </div>
  )
}
